import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ApiTask } from '~/api/types';
import { Task } from '~/data-classes';
import { useTaskPairing } from '~/hooks';
import {
    selectIsEquipmentDepotPairing,
    setIsOpenPairEquipmentControlPopup
} from '~/reducers/mapDrawerSettingsSlice';
import {
    resetSelectedTaskIds,
    selectSelectedTaskIds
} from '~/reducers/selectedTaskIdsSlice';
import { addToast } from '~/reducers/toastsSlice';

export const useHandleTaskPairing = () => {
    const dispatch = useDispatch();

    const { t } = useTranslation(['error']);

    const [selectedTaskId] = useSelector(selectSelectedTaskIds);
    const isEquipmentDepotPairing = useSelector(selectIsEquipmentDepotPairing);

    const { manualPairing, depotPairing } = useTaskPairing();

    const updateTaskStates = useCallback(() => {
        dispatch(setIsOpenPairEquipmentControlPopup(false));
        dispatch(resetSelectedTaskIds(null));
    }, [dispatch]);

    const handleTaskPairing = useCallback(
        (newTask: ApiTask) => {
            try {
                const taskIds = [newTask.id, selectedTaskId];
                const apiTask = new Task(newTask);

                if (isEquipmentDepotPairing) {
                    depotPairing({
                        selectedTaskIds: taskIds,
                        updateTaskStatesForDepotPairing: updateTaskStates
                    });
                    return;
                }
                manualPairing({
                    selectedTaskIds: taskIds,
                    updateTaskStates,
                    newTask: apiTask
                });
            } catch (e) {
                const message = isEquipmentDepotPairing
                    ? t('depotPairError.general')
                    : t('manualPairError.general');

                dispatch(
                    addToast({
                        message,
                        variant: 'error'
                    })
                );
            }
        },
        [
            depotPairing,
            dispatch,
            isEquipmentDepotPairing,
            manualPairing,
            selectedTaskId,
            updateTaskStates,
            t
        ]
    );

    return {
        handleTaskPairing,
        updateTaskStates
    };
};
